/**
 * Secret Validation Schemas
 *
 * Validation schemas for secret management operations including creation,
 * updates, and access grants.
 *
 * SECURITY NOTES:
 * - Secret values are encrypted via tenant KMS keys on backend
 * - Secret values are NEVER returned via API (write-only from UI)
 * - Only applications with grants can access secret values at runtime
 */

import { z } from 'zod';
import { nonEmptyStringSchema, descriptionSchema, uuidSchema } from './common';

// ============================================================================
// SECRET CODE VALIDATION
// ============================================================================

/**
 * Secret code validation schema
 * Format: lowercase alphanumeric and hyphens only
 * Length: 3-50 characters
 * Examples: "stripe-api-key", "openai-token", "slack-webhook"
 */
export const secretCodeSchema = z
  .string({
    required_error: 'Secret code is required',
    invalid_type_error: 'Secret code must be a string',
  })
  .min(3, 'Code must be at least 3 characters long')
  .max(50, 'Code must not exceed 50 characters')
  .regex(
    /^[a-z0-9-]+$/,
    'Code must contain only lowercase letters, numbers, and hyphens'
  );

export type SecretCodeValidation = z.infer<typeof secretCodeSchema>;

// ============================================================================
// SECRET VALUE VALIDATION
// ============================================================================

/**
 * Secret value validation schema
 * Validates the plain text value before encryption
 * Length: 1-10000 characters (to accommodate various API keys, tokens, etc.)
 */
export const secretValueSchema = z
  .string({
    required_error: 'Secret value is required',
    invalid_type_error: 'Secret value must be a string',
  })
  .min(1, 'Secret value cannot be empty')
  .max(10000, 'Secret value must not exceed 10000 characters');

export type SecretValueValidation = z.infer<typeof secretValueSchema>;

// ============================================================================
// SECRET CREATE VALIDATION
// ============================================================================

/**
 * Secret creation schema
 * Used when creating a new secret for a tenant
 *
 * Required fields:
 * - secretCode: Unique code identifier (lowercase alphanumeric + hyphens)
 * - secretName: Display name for the secret
 * - value: Plain text value (will be encrypted)
 *
 * Optional fields:
 * - description: Secret description
 */
export const secretCreateSchema = z
  .object({
    secretCode: secretCodeSchema,
    secretName: nonEmptyStringSchema
      .min(1, 'Secret name must be at least 1 character long')
      .max(100, 'Secret name must not exceed 100 characters'),
    description: descriptionSchema,
    value: secretValueSchema,
  })
  .strict();

export type SecretCreateValidation = z.infer<typeof secretCreateSchema>;

/**
 * Form-specific secret creation schema
 * Includes auto-generate toggle for code
 */
export const createSecretFormSchema = z
  .object({
    secretName: nonEmptyStringSchema
      .min(1, 'Secret name is required')
      .max(100, 'Secret name must not exceed 100 characters'),
    secretCode: secretCodeSchema,
    description: z.string().max(1000, 'Description must not exceed 1000 characters').optional(),
    value: secretValueSchema,
  });

export type CreateSecretFormData = z.infer<typeof createSecretFormSchema>;

// ============================================================================
// SECRET UPDATE VALIDATION
// ============================================================================

/**
 * Secret update schema
 * Used for updating secret information
 *
 * All fields are optional (at least one must be provided)
 * Note: secretCode is immutable and cannot be updated
 * Note: value is optional - if not provided, only metadata is updated
 */
export const secretUpdateSchema = z
  .object({
    secretName: nonEmptyStringSchema
      .min(1, 'Secret name must be at least 1 character long')
      .max(100, 'Secret name must not exceed 100 characters')
      .optional(),
    description: descriptionSchema,
    value: secretValueSchema.optional(),
  })
  .strict()
  .refine(
    (data) => Object.keys(data).length > 0,
    'At least one field must be provided for update'
  );

export type SecretUpdateValidation = z.infer<typeof secretUpdateSchema>;

/**
 * Form-specific secret update schema
 */
export const updateSecretFormSchema = z
  .object({
    secretName: nonEmptyStringSchema
      .min(1, 'Secret name is required')
      .max(100, 'Secret name must not exceed 100 characters'),
    description: z.string().max(1000, 'Description must not exceed 1000 characters').optional(),
    value: z.string().max(10000, 'Secret value must not exceed 10000 characters').optional(),
  });

export type UpdateSecretFormData = z.infer<typeof updateSecretFormSchema>;

// ============================================================================
// SECRET ACCESS GRANT VALIDATION
// ============================================================================

/**
 * Secret access grant schema
 * Used when granting an application access to a secret
 *
 * Required fields:
 * - applicationId: ID of the application to grant access to
 *
 * Note: Secrets have binary access (read-only) - either accessible or not
 * No granular permissions like volumes
 */
export const secretAccessGrantSchema = z
  .object({
    applicationId: uuidSchema,
  })
  .strict();

export type SecretAccessGrantValidation = z.infer<typeof secretAccessGrantSchema>;

/**
 * Secret access revoke schema
 * Used when revoking an application's access to a secret
 */
export const secretAccessRevokeSchema = z
  .object({
    applicationId: uuidSchema,
  })
  .strict();

export type SecretAccessRevokeValidation = z.infer<typeof secretAccessRevokeSchema>;

// ============================================================================
// SECRET QUERY VALIDATION
// ============================================================================

/**
 * Secret list query parameters schema
 */
export const secretListQuerySchema = z
  .object({
    search: z
      .string({
        invalid_type_error: 'Search must be a string',
      })
      .min(1, 'Search term must be at least 1 character')
      .max(100, 'Search term must not exceed 100 characters')
      .optional(),
    limit: z
      .string()
      .transform((val) => parseInt(val, 10))
      .pipe(z.number().int().min(1).max(100))
      .optional(),
  })
  .strict();

export type SecretListQueryValidation = z.infer<typeof secretListQuerySchema>;
